import { router } from '@inertiajs/react';
import { Calendar, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toastFlash } from '@/lib/flash-toast';
import type { MentorAdmin } from '@/types';
import { EditMentorDialog } from './EditMentorDialog';
import { githubUsername } from './githubUsername';

interface MentorsTableProps {
  eventSlug: string;
  mentors: MentorAdmin[];
}

const DAY_ORDER: Record<string, number> = {
  friday: 0,
  saturday: 1,
  sunday: 2,
};
const DAY_NAMES: Record<string, string> = {
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

function getInitials(name: string) {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function formatAvailability(availability: MentorAdmin['availability']) {
  if (!availability || availability.length === 0) {
    return 'No schedule set';
  }

  const sorted = [...availability].sort(
    (a, b) => DAY_ORDER[a.day] - DAY_ORDER[b.day],
  );

  return sorted.map((slot) => (
    <div key={`${slot.day}-${slot.startTime}`} className="text-sm">
      <span className="font-medium">{DAY_NAMES[slot.day]}</span>:{' '}
      {slot.startTime} - {slot.endTime}
      {slot.tentative && (
        <Badge variant="outline" className="ml-2">
          TBC
        </Badge>
      )}
    </div>
  ));
}

export function MentorsTable({ eventSlug, mentors }: MentorsTableProps) {
  const [isPending, setIsPending] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const editMentor = mentors.find((mentor) => mentor.id === editId) ?? null;

  const handleDelete = () => {
    if (!deleteId) return;

    setIsPending(true);
    router.delete(`/admin/${eventSlug}/mentors/${deleteId}`, {
      preserveScroll: true,
      onSuccess: (page) => toastFlash(page, 'Mentor deleted successfully'),
      onError: () =>
        toast({ type: 'error', description: 'Failed to delete mentor' }),
      onFinish: () => {
        setIsPending(false);
        setDeleteId(null);
      },
    });
  };

  if (mentors.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No mentors found. Create one to get started.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Name</TableHead>
              <TableHead>GitHub</TableHead>
              <TableHead>LinkedIn</TableHead>
              <TableHead>Company / Title</TableHead>
              <TableHead>Availability</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mentors.map((mentor) => (
              <TableRow key={mentor.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={mentor.pictureUrl || undefined}
                        alt={mentor.fullName}
                      />
                      <AvatarFallback>
                        {getInitials(mentor.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{mentor.fullName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <a
                    href={mentor.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                  >
                    {githubUsername(mentor.github)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
                <TableCell>
                  {mentor.linkedin ? (
                    <a
                      href={mentor.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      LinkedIn
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {mentor.companyTitle || (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {mentor.availability && mentor.availability.length > 0 ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Calendar className="mr-2 h-4 w-4" />
                          {mentor.availability.length} slot
                          {mentor.availability.length !== 1 ? 's' : ''}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64">
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm">Schedule</h4>
                          {formatAvailability(mentor.availability)}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      No schedule
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditId(mentor.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(mentor.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mentor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this mentor? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editMentor && (
        <EditMentorDialog
          key={editMentor.id}
          eventSlug={eventSlug}
          mentor={editMentor}
          onClose={() => setEditId(null)}
        />
      )}
    </>
  );
}
